import {CSSProperties, FC, PropsWithChildren} from 'react'

import {SKELETON_LOADING_TRANSITION} from '@/theme/cssTransitions'
import {theme} from '@/theme/theme'
import {useUniqueClassName} from '@/utils/hooks/useUniqueClassName'

type SkeletonLoadingOverlayProps = PropsWithChildren<{
  borderRadius?: CSSProperties['borderRadius']
  highlightColor?: string
}>

export const SkeletonLoadingOverlay: FC<SkeletonLoadingOverlayProps> = ({
  borderRadius = 0,
  highlightColor = theme.palette.skeletonLoading.highlight.onLight,
  children,
}) => {
  const className = useUniqueClassName('skeleton-loading')

  return (
    <>
      <div className={className}>
        {children}
        <span className={`${className}-overlay`} />
      </div>

      <style jsx global>{`
        @keyframes ${className}-sweep {
          from {
            transform: translateX(${'-120%'});
          }
          to {
            transform: translateX(${'120%'});
          }
        }
        .${className} {
          position: relative;
          overflow: hidden;
          border-radius: ${typeof borderRadius === 'number' ? `${borderRadius}px` : borderRadius};
        }
        .${className}-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(
            90deg,
            transparent 0%,
            ${highlightColor} 30%,
            transparent 100%
          );
          pointer-events: none;
          animation-name: ${className}-sweep;
          animation-duration: ${SKELETON_LOADING_TRANSITION}s;
          animation-timing-function: ease-in-out;
          animation-iteration-count: infinite;
        }
        /* The global reduced-motion rule would stop the sweep mid-skeleton as a static stripe */
        @media (prefers-reduced-motion: reduce) {
          .${className}-overlay {
            display: none;
          }
        }
      `}</style>
    </>
  )
}
